import { sortBy } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import Highcharts from 'highcharts';
import Chart from '../CommonComponents/Chart';

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 24px;
  background: #ffffff;
  border: 1px solid #cccccc;
  box-sizing: border-box;
  box-shadow: 0 2px 4px rgba(184, 184, 184, 0.08);

  > * {
    padding: 0 5px;
    width: 40%;

    &:first-child {
      width: 20%;
    }
  }
`;

const Title = styled.div`
  font-weight: 600;
  color: #444;
  margin-bottom: 24px;
`;

function createRiskalyzeLineChart(portfolio) {
  const bestWorstFormatter = function() {
    const sign = this.point.percent > 0 ? '+' : '-';
    return `${sign +
      Highcharts.numberFormat(
        Math.abs(this.point.percent),
        2,
      )}%<br />${sign}$${Highcharts.numberFormat(Math.abs(this.point.dollarValue), 0)}`;
  };

  const { analysis } = portfolio;
  const { total } = portfolio;

  const points: any = {
    best: {
      y: 1,
      percent: analysis.best * 100,
      dollarValue: analysis.best * total,
      color: 'green',
      dataLabels: {
        enabled: true,
        y: 65,
        formatter: bestWorstFormatter,
        style: { color: 'green' },
      },
    },
    worst: {
      y: -1,
      percent: analysis.worst * 100,
      dollarValue: analysis.worst * total,
      color: 'red',
      dataLabels: { enabled: true, y: 65, formatter: bestWorstFormatter, style: { color: 'red' } },
    },
    portfolio: {
      y: 0,
      x: total,
      color: 'gray',
      dataLabels: { enabled: true, y: -15, format: '${x:,.0f}' },
    },
  };

  const endLine1Base = sortBy(
    [
      { name: 'best', value: Math.abs(analysis.best) },
      { name: 'worst', value: Math.abs(analysis.worst) },
    ],
    ({ value }) => value,
  ).reverse()[0].name;

  points.endLine1 = {
    y: points[endLine1Base].y * 2,
    dollarValue: points[endLine1Base].dollarValue * 2,
    marker: { enabled: false, states: { hover: { enabled: false } } },
  };

  points.endLine2 = {
    y: points.endLine1.y * -1,
    dollarValue: points.endLine1.dollarValue * -1,
    marker: { enabled: false, states: { hover: { enabled: false } } },
  };

  Object.entries(points).forEach(function([key, value]) {
    if (key !== 'portfolio') {
      (value as any).x = (value as any).dollarValue + total;
    }
  });

  const slope = (points.worst.y - points.best.y) / (points.worst.x - points.best.x);

  Object.entries(points).forEach(function([key, value]) {
    if (['portfolio', 'endLine1', 'endLine2'].includes(key)) {
      (value as any).y = slope * ((value as any).x - points.best.x) + points.best.y;
    }
  });

  return {
    chart: { backgroundColor: 'transparent', height: 260, width: 260 },
    credits: { enabled: false },
    plotOptions: { series: { marker: { radius: 6 } } },
    xAxis: {
      categories: null,
      gridLineColor: 'transparent',
      labels: { enabled: false },
      lineWidth: 0,
      minorGridLineWidth: 0,
      lineColor: 'transparent',
      minorTickLength: 0,
      tickLength: 0,
    },
    yAxis: {
      title: { text: '' },
      plotLines: [{ value: 0, width: 0, color: '#808080' }],
      labels: { enabled: false },
      gridLineColor: 'transparent',
    },
    tooltip: {
      valueSuffix: '',
      formatter() {
        return `$${Highcharts.numberFormat(this.x, 0)}`;
      },
    },
    legend: {
      enabled: false,
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
      borderWidth: 0,
    },
    series: [
      { color: '#CCCCCC', data: sortBy(Object.values(points), ({ x }: { x: number }) => x) },
    ],
    title: {
      text: '95% Probability Range<br />(6 Months)',
      useHTML: true,
      floating: true,
      align: 'left',
    },
    subtitle: { text: '' },
  };
}

interface RiskModel {
  analysis: {
    best: number;
    worst: number;
    riskNumber: number;
  };
  total: number;
}
interface Props {
  riskNumber: number | null;
  currentPortfolio: RiskModel | null;
  recommendedPortfolio: RiskModel | null;
  style?: React.CSSProperties;
}

export const RiskProfileSummary: React.FC<Props> = ({
  riskNumber,
  style,
  currentPortfolio,
  recommendedPortfolio,
}) => {
  return (
    <Wrapper style={style}>
      <div>
        <Title>Your Risk Number</Title>
        {riskNumber !== null ? (
          <img
            style={{ width: 115 }}
            src={`https://1dc018be4716da4aaa18-5d73971c6ce0cd6d739efc4134c4b48a.ssl.cf2.rackcdn.com/v2/standard/90/r${riskNumber}.png`}
            alt="Overall Risk"
          />
        ) : (
          <div>No risk number assigned in Riskalyze</div>
        )}
      </div>
      <div>
        <Title>Your Portfolio</Title>
        {currentPortfolio ? (
          <div>
            <img
              style={{ width: 80, verticalAlign: 'top', marginRight: 16 }}
              src={`https://1dc018be4716da4aaa18-5d73971c6ce0cd6d739efc4134c4b48a.ssl.cf2.rackcdn.com/v2/portfolio/90/r${currentPortfolio.analysis.riskNumber}.png`}
              alt="Portfolio Risk"
            />
            <div style={{ display: 'inline-block' }}>
              <Chart options={createRiskalyzeLineChart(currentPortfolio)} />
            </div>
          </div>
        ) : (
          <div>No portfolio assigned in Riskalyze</div>
        )}
      </div>
      <div>
        <Title>Model</Title>
        {recommendedPortfolio ? (
          <div>
            <img
              style={{ width: 80, verticalAlign: 'top', marginRight: 16 }}
              src={`https://1dc018be4716da4aaa18-5d73971c6ce0cd6d739efc4134c4b48a.ssl.cf2.rackcdn.com/v2/target/90/r${recommendedPortfolio.analysis.riskNumber}.png`}
              alt="Model Risk"
            />
            <div style={{ display: 'inline-block' }}>
              <Chart options={createRiskalyzeLineChart(recommendedPortfolio)} />
            </div>
          </div>
        ) : (
          <div>No model assigned in Riskalyze</div>
        )}
      </div>
    </Wrapper>
  );
};
